import { useSharedAlarm } from '@hakimo-ui/hakimo/data-access';
import { DataVersion } from '@hakimo-ui/hakimo/types';
import { NotFound, QueryResult } from '@hakimo-ui/hakimo/ui-elements';
import { parseDataVersionParam } from '@hakimo-ui/hakimo/util';
import { Alert } from '@hakimo-ui/shared/ui-base';
import { useSearchParams } from 'react-router-dom';
import { SharedRawAlarmDetails } from '../scan-raw-alarms/SharedRawAlarmRoot';
import AlarmDetails from './AlarmDetails';

interface Props {
  sharedToken: string;
  partitionKey?: string;
  dataVersion?: DataVersion;
}

function SharedAlarmDetails(props: Props) {
  const { sharedToken, partitionKey, dataVersion } = props;

  const queryResult = useSharedAlarm(sharedToken, partitionKey, dataVersion);
  const { refetch } = queryResult;

  return (
    <QueryResult
      queryResult={queryResult}
      errorFormatter={() => (
        <div className="mx-auto max-w-screen-2xl p-6">
          <Alert type="error">
            This link is expired or invalid. Please ask the sender for a new
            link.
          </Alert>
        </div>
      )}
    >
      {(data) => (
        <AlarmDetails
          alarm={data}
          sharedToken={sharedToken}
          onRefresh={() => refetch()}
          dataVersion={dataVersion}
        />
      )}
    </QueryResult>
  );
}

export function SharedAlarmRoot() {
  const [searchParams] = useSearchParams();
  const sharedToken = searchParams.get('sharedToken');
  const partitionKey = searchParams.get('partitionKey') ?? undefined;
  const product = searchParams.get('product');
  // The share link carries its own ?version=v3. This page is reachable by
  // external viewers and has no v2/v3 toggle of its own, so the data version
  // must come from the URL only — never from dataVersionOverrideAtom, which is
  // the list page's localStorage state and unrelated to this alarm.
  const dataVersion = parseDataVersionParam(searchParams);

  if (!sharedToken) {
    return <NotFound message="Missing shared token in shared alarm URL" />;
  }

  return product === 'scan' ? (
    <SharedRawAlarmDetails sharedToken={sharedToken} />
  ) : (
    <SharedAlarmDetails
      sharedToken={sharedToken}
      partitionKey={partitionKey}
      dataVersion={dataVersion}
    />
  );
}

export default SharedAlarmRoot;
